import { cn } from "@/lib/utils";
import { IconPhotoUp, IconPlus } from "@tabler/icons-react";

type Variant = "default" | "dialog" | "media-dialog";

type ButtonProps = {
  getInputProps: () => any;
  openImageUpload: () => void;
  openImageSelectorDialog: () => void;
  isDragActive: boolean;
  isImagesEmpty: boolean;
  variant?: Variant;
  className?: string;
};

export function ImageUploadButton(props: ButtonProps) {
  const variant = props.variant || "default";

  if (props.isImagesEmpty || props.isDragActive) {
    return <ImageUploadArea {...props} variant={variant} />;
  }

  return (
    <div className={cn("flex", props.className)}>
      <button
        className={cn(
          "flex cursor-pointer items-center justify-center overflow-hidden border border-dashed border-border bg-file-upload hover:bg-file-upload-hover",
          variant === "default"
            ? "h-40 flex-[0_0_250px] rounded-xl"
            : "aspect-square h-full w-full overflow-hidden rounded-md object-cover",
        )}
        onClick={props.openImageSelectorDialog}
      >
        <IconPlus stroke={1.5} size={32} className="text-text-secondary" />
      </button>
      <div className="flex-1"></div>
    </div>
  );
}

const defaultText = {
  primary: "Click to upload images",
  primaryDrag: "Drop files",
  secondary: "or drag and drop them here",
  secondaryDrag: "to add them to the video",
};

const variantText: Record<
  Variant,
  {
    primary: string;
    primaryDrag: string;
    secondary: string;
    secondaryDrag: string;
  }
> = {
  default: defaultText,
  dialog: defaultText,
  "media-dialog": {
    primary: "Click to upload media",
    primaryDrag: "Drop media here",
    secondary: "or drag and drop images and videos",
    secondaryDrag: "to add them to your library",
  },
};

export function ImageUploadArea({
  getInputProps,
  openImageUpload,
  isDragActive,
  isImagesEmpty,
  variant = "default",
  className,
}: Omit<ButtonProps, "openImageSelectorDialog">) {
  const text = variantText[variant];
  const primaryText = isImagesEmpty ? text.primary : text.primaryDrag;
  const secondaryText = isImagesEmpty ? text.secondary : text.secondaryDrag;

  return (
    <button
      onClick={openImageUpload}
      className={cn(
        "flex flex-1 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed",
        {
          "absolute top-1/2 left-1/2 z-40 h-full w-full -translate-x-1/2 -translate-y-1/2 transform rounded-none":
            variant === "dialog" || variant === "media-dialog",
          "rounded-br-2xl":
            (variant === "dialog" || variant === "media-dialog") &&
            isImagesEmpty,
        },
        isDragActive
          ? "border-primary-400 bg-primary-100"
          : "border-border bg-file-upload hover:bg-file-upload-hover",
        className,
      )}
    >
      <input {...getInputProps()} className="h-full w-full" />
      <IconPhotoUp stroke={1.5} size={32} color="url(#icon-gradient)" />
      <div className="h-4" />
      <h1 className="text-base font-semibold">{primaryText}</h1>
      <p className="text-sm text-text-secondary">{secondaryText}</p>
    </button>
  );
}
